import React from 'react';
import { Button, Modal, Space } from 'antd';
import { styled } from 'styled-components';
import {
  isJobChainTemplateApiFailure,
  isJobChainTemplateProblemDetails,
} from '@/api/JobChainTemplates';
import { type TFunc, useTranslate } from '@/translations';

export const templateListPath = '/admin/jobs/job-chain-templates';
export const EditorShell = styled.main`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
  color: #202d3d;
  h1 {
    font-size: 26px;
    margin: 16px 0 8px;
  }
  h2 {
    font-size: 19px;
  }
  h3 {
    font-size: 16px;
  }
  section,
  fieldset {
    border: 1px solid #d5deea;
    border-radius: 8px;
    padding: 16px;
    background: white;
    margin: 12px 0;
    min-width: 0;
  }
  label {
    display: grid;
    gap: 5px;
    margin: 10px 0;
    font-weight: 500;
  }
  input,
  select {
    border: 1px solid #9daec1;
    border-radius: 4px;
    padding: 8px;
    max-width: 100%;
    background: white;
    color: inherit;
  }
  button {
    border: 1px solid #9daec1;
    border-radius: 5px;
    padding: 8px 12px;
    margin: 4px;
    background: white;
    color: #174c7c;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.5;
    cursor: default;
  }
  button[data-primary='true'] {
    background: #164f80;
    color: white;
  }
  :focus-visible {
    outline: 3px solid #1677ff;
    outline-offset: 3px;
  }
  [role='alert'] {
    border-left: 4px solid #b63e28;
    padding: 12px;
    background: #fff4ed;
  }
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .workspace {
    display: grid;
    grid-template-columns: minmax(240px, 1fr) minmax(320px, 2fr);
    gap: 16px;
    align-items: start;
  }
  .graph-view {
    overflow: auto;
    max-height: 440px;
  }
  .modal-shade {
    position: fixed;
    inset: 0;
    z-index: 1200;
    background: #0006;
    display: grid;
    place-items: center;
  }
  [role='dialog'] {
    width: min(560px, 95vw);
    padding: 24px;
    border-radius: 10px;
    background: white;
  }
  @media (max-width: 800px) {
    .workspace {
      grid-template-columns: 1fr;
    }
  }
`;

export const templateFailureText = (error: unknown, t: TFunc) => {
  if (
    isJobChainTemplateApiFailure(error) &&
    isJobChainTemplateProblemDetails(error.message)
  ) {
    const problem = error.message;
    return [
      problem.detail || t('jobChainTemplateEditorRequestFailed'),
      problem.code,
      problem.correlationId &&
        t('jobChainTemplateEditorReference', {
          correlationId: problem.correlationId,
        }),
    ]
      .filter(Boolean)
      .join(' ');
  }
  return t('jobChainTemplateEditorConnectivityFailure');
};

export const ConfirmDialog = ({
  title,
  children,
  confirm,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: React.ReactNode;
  confirm: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslate();
  return (
    <Modal
      open
      title={title}
      width={560}
      onCancel={onCancel}
      maskClosable={false}
      closable={false}
      footer={
        <Space>
          <Button autoFocus onClick={onCancel}>
            {t('jobChainTemplateEditorKeepEditing')}
          </Button>
          <Button type="primary" onClick={onConfirm}>
            {confirm}
          </Button>
        </Space>
      }
    >
      {children}
    </Modal>
  );
};
